import { useState, useEffect } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { NAV_ITEMS, ROLE_LABELS, SECTION_COLORS, userPages } from "@/lib/roles";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Moon, Sun, LogOut, Hotel, Menu } from "lucide-react";
import PropertySwitcher from "@/components/PropertySwitcher";
import { QuickReminder } from "@/components/QuickReminder";

function useTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem("gcg-theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("gcg-theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, setDark];
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dark, setDark] = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [comms, setComms] = useState({ unread_chat: 0, new_requests: 0, hk_pending: 0 });
  const [reqUnread, setReqUnread] = useState(0);
  const [roleMap, setRoleMap] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/roles").then((r) => {
      const m = {};
      r.data.forEach((role) => { m[role.key] = role; });
      setRoleMap(m);
    }).catch(() => setRoleMap({}));
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const fetchComms = () => api.get("/comms/summary").then((r) => setComms(r.data)).catch(() => {});
    const fetchReqUnread = () => api.get("/requests/unread-count").then((r) => setReqUnread(r.data.count || 0)).catch(() => {});
    fetchComms();
    fetchReqUnread();
    const t = setInterval(() => { fetchComms(); fetchReqUnread(); }, 20000);
    window.addEventListener("comms-refresh", fetchComms);
    return () => { clearInterval(t); window.removeEventListener("comms-refresh", fetchComms); };
  }, [user, location.pathname]);

  // Mark request responses as seen when the user opens the Requests page
  useEffect(() => {
    if (!user || location.pathname !== "/requests") return;
    api.post("/requests/seen").then(() => setReqUnread(0)).catch(() => {});
  }, [user, location.pathname]);

  const badgeFor = (to) => (to === "/chat" ? comms.unread_chat : to === "/requests" ? comms.new_requests : to === "/housekeeping" ? comms.hk_pending : to === "/tasks" ? comms.tasks : to === "/daily-reports" ? comms.daily_report_overdue : 0);

  const myRole = roleMap?.[user?.role];
  const allowedPages = userPages(user, myRole);
  const items = NAV_ITEMS.filter((i) => allowedPages.includes(i.to));
  const pageBase = "/" + (location.pathname.split("/")[1] || "");
  const pageBlocked = Array.isArray(user?.page_access) && pageBase !== "/" && NAV_ITEMS.some((i) => i.to === pageBase) && !allowedPages.includes(pageBase);
  const roleLabel = myRole?.label || ROLE_LABELS[user?.role] || "User";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user?.name || user?.email || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="flex min-h-screen bg-background">
      <div className="grain" />
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-border bg-card transition-transform duration-200 lg:static lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-16 shrink-0 items-center gap-2 border-b border-border px-6">
          <div className="flex h-8 w-8 items-center justify-center bg-primary text-primary-foreground">
            <Hotel className="h-4 w-4" />
          </div>
          <div className="leading-tight">
            <div className="font-heading text-sm font-black tracking-tight">GCG HOTELS</div>
            <div className="label-caps text-[0.55rem] text-muted-foreground">Operations</div>
          </div>
        </div>
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-3" data-testid="main-nav">
          {items.map((item, idx) => {
            const Icon = item.icon;
            const showSection = item.section && item.section !== (items[idx - 1]?.section);
            const sectionColor = SECTION_COLORS[item.section];
            return (
              <div key={item.to}>
                {showSection && (
                  <div className="mb-1 mt-4 flex items-center gap-2 px-3" data-testid={`nav-section-${item.section.toLowerCase()}`}>
                    <span className="h-2.5 w-0.5 rounded-full" style={{ backgroundColor: sectionColor }} />
                    <span className="label-caps text-[0.55rem] font-semibold" style={{ color: sectionColor }}>{item.section}</span>
                    <span className="ml-1 h-px flex-1" style={{ background: `linear-gradient(to right, ${sectionColor}55, transparent)` }} />
                  </div>
                )}
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : (item.to === "/requests" && reqUnread > 0)
                        ? "animate-pulse bg-amber-500/20 text-amber-700 ring-1 ring-amber-400 dark:text-amber-300"
                        : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className="relative flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors"
                        style={isActive ? undefined : { backgroundColor: `${item.color}1f`, color: item.color }}
                      >
                        <Icon className="h-4 w-4" />
                        {item.to === "/requests" && reqUnread > 0 && (
                          <span className="absolute -right-1 -top-1 h-2.5 w-2.5 animate-ping rounded-full bg-amber-500" data-testid="nav-requests-dot" />
                        )}
                      </span>
                      {item.label}
                      {item.to === "/requests" && reqUnread > 0 ? (
                        <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-[0.65rem] font-bold text-white" data-testid="nav-requests-unread" title="New responses on requests">
                          {reqUnread}
                        </span>
                      ) : badgeFor(item.to) > 0 && (
                        <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[0.65rem] font-bold text-destructive-foreground" data-testid={`nav-badge-${item.to.slice(1)}`}>
                          {badgeFor(item.to)}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              </div>
            );
          })}
        </nav>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-border bg-card/80 px-4 backdrop-blur-xl lg:px-8">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div className="label-caps text-muted-foreground">
              {roleLabel}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {["admin", "regional"].includes(user?.role) && <QuickReminder />}
            <PropertySwitcher />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDark(!dark)}
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex h-9 w-9 items-center justify-center bg-primary text-xs font-bold text-primary-foreground"
                  data-testid="user-menu-btn"
                >
                  {initials}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user?.name}</div>
                  <div className="text-xs font-normal text-muted-foreground">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="relative z-10 flex-1 p-4 lg:p-8">
          {pageBlocked ? (
            <div className="mx-auto mt-24 max-w-md border border-border p-8 text-center" data-testid="page-no-access">
              <div className="font-heading text-2xl font-black">No access</div>
              <p className="mt-2 text-sm text-muted-foreground">You don't have access to this page. Ask an Administrator if you need it.</p>
            </div>
          ) : <Outlet />}
        </main>
      </div>
    </div>
  );
}
